import React, { useState } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import {
  StandingsSidebar,
  LiveAnswer as SharedLiveAnswer,
  AnswerOptions,
  ImageWithFallback,
  QuestionDisplay,
  TimerSection,
} from '../shared';
import { useQuestionFlip, useTimerFlipTrigger } from '../../hooks/useQuestionFlip';
import { Question } from '../../types/game';
import type { GameStatistics } from '../../types/game';
import { usePartyHostGameLifecycle } from '../../hooks/usePartyHostGameLifecycle';
import {
  PartyHostCorrectAnswerResult,
  PartyHostGameEnd,
  PartyHostPlayingChrome,
  PartyHostWaiting,
} from './PartyHostShell';
import { partyHostCssTheme } from '../../themes/cssTheme';
import '../../App.css';
import '../../styles/game-layout.css';
import { useTranslation } from '../../i18n';

interface KinkyOrNotQuestion {
  questionId?: string;
  id?: string;
  question: string;
  options: string[];
  timeLimit: number;
  questionNumber: number;
  totalQuestions: number;
  startTime: number;
  imageUrl?: string;
  fallbackImageUrl?: string;
  isPicturePerfect?: boolean;
  isKinkyOrNot?: boolean;
  interesting?: string;
}

interface QuestionResult {
  questionId: string;
  correctAnswer: string;
  submissions: Array<{
    playerId: string;
    answer: string;
    responseTime: number;
    isCorrect: boolean;
    points: number;
  }>;
  leaderboard: Array<{
    playerId: string;
    playerName: string;
    score: number;
  }>;
  isKinkyOrNot?: boolean;
}

interface KinkyOrNotHostDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{
      id: string;
      name: string;
      score: number;
      avatar?: string;
    }>;
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: KinkyOrNotQuestion;
    finalLeaderboard?: Array<{ playerId: string; playerName: string; score: number }>;
    gameStatistics?: GameStatistics | null;
  };
  socket: WebSocketAdapter;
  onBackToGameSelection?: () => void;
  myPlayerId?: string;
  hostIsPlaying?: boolean;
  onGoHome?: () => void;
}

const THEME = partyHostCssTheme;

const KinkyOrNotHostDisplay: React.FC<KinkyOrNotHostDisplayProps> = ({
  roomState,
  socket,
  onBackToGameSelection,
  hostIsPlaying = false,
}) => {
  const { t } = useTranslation();
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [answerCorrect, setAnswerCorrect] = useState<boolean | null>(null);
  const {
    currentQuestion,
    timeRemaining,
    questionResult,
    gameEnded,
    finalLeaderboard,
    gameStatistics,
    waitingForNewGame,
    liveAnswers,
    answerSubmitted,
    gameEndData,
    submitAnswer,
    restartGame: handleRestartGame,
    playSameGame,
    setAnswerSubmitted,
  } = usePartyHostGameLifecycle<
    KinkyOrNotQuestion,
    QuestionResult,
    { questionId: string; isCorrect: boolean; points: number }
  >({
    roomState,
    socket,
    hostIsPlaying,
    isQuestionForGame: (question) => !!question.isKinkyOrNot || !!question.isPicturePerfect,
    isResultForGame: (result) => !!result.isKinkyOrNot,
    onResetHostPlaying: () => {
      setSelectedAnswer(null);
      setAnswerCorrect(null);
    },
    onAnswerReceived: (data) => {
      if (!hostIsPlaying) return;
      setAnswerCorrect(data.isCorrect);
      setAnswerSubmitted(true);
    },
  });

  const flipState = useQuestionFlip({
    currentQuestion,
    liveAnswers,
    totalPlayers: roomState.players.length,
    answerSubmitted,
    hostIsPlaying,
  });

  useTimerFlipTrigger(
    timeRemaining,
    currentQuestion,
    flipState.showQuestionFlip,
    flipState.setShowQuestionFlip,
    flipState.previousQuestionIdForFlipRef
  );

  const handleAnswerClick = (answer: string) => {
    if (answerSubmitted || !currentQuestion) return;
    setSelectedAnswer(answer);
    if (!submitAnswer(answer)) setSelectedAnswer(null);
  };

  const sharedLiveAnswers: SharedLiveAnswer[] = liveAnswers.map((la) => ({
    playerId: la.playerId,
    playerName: la.playerName,
    avatar: la.avatar,
    timestamp: la.timestamp,
    answer: la.answer,
  }));

  if (gameEnded) {
    return (
      <PartyHostGameEnd
        roomName={roomState.roomName}
        gameType={roomState.gameType}
        finalLeaderboard={finalLeaderboard}
        gameStatistics={gameStatistics}
        gameEndData={gameEndData}
        onPlaySameGame={playSameGame}
        onBackToGameSelection={onBackToGameSelection}
      />
    );
  }

  if (waitingForNewGame || (!currentQuestion && !questionResult)) {
    return (
      <PartyHostWaiting
        roomName={roomState.roomName}
        emoji="🔥"
        onBackToGameSelection={onBackToGameSelection}
      />
    );
  }

  if (questionResult) {
    const resultPlayers = questionResult.leaderboard.map((p) => {
      const roomPlayer = roomState.players.find((rp) => rp.id === p.playerId);
      return { id: p.playerId, name: p.playerName, score: p.score, avatar: roomPlayer?.avatar };
    });
    return (
      <PartyHostCorrectAnswerResult
        roomName={roomState.roomName}
        correctAnswer={questionResult.correctAnswer}
        onBackToGameSelection={onBackToGameSelection}
        standings={{
          players: resultPlayers,
          liveAnswers: sharedLiveAnswers,
          questionId: questionResult.questionId,
          theme: THEME,
        }}
      />
    );
  }

  const progress = currentQuestion ? (timeRemaining / currentQuestion.timeLimit) * 100 : 0;
  const secondsRemaining = Math.ceil(timeRemaining / 1000);

  return (
    <PartyHostPlayingChrome
      gameLabel="🔥 Kinky or Not?"
      roomName={roomState.roomName}
      theme={THEME}
      onBackToGameSelection={onBackToGameSelection}
      onRestart={handleRestartGame}
    >
      {currentQuestion && (
        <div className="game-host-layout">
          <div
            className={`game-host-left ${!currentQuestion.imageUrl && !currentQuestion.fallbackImageUrl ? 'no-image' : ''} ${!hostIsPlaying ? 'host-only' : ''}`}
          >
            <div className="game-host-timer-section">
              <TimerSection
                questionNumber={currentQuestion.questionNumber}
                totalQuestions={currentQuestion.totalQuestions}
                timeSeconds={secondsRemaining}
                progress={progress}
                primaryColor={THEME.primary}
              />
            </div>

            {(currentQuestion.imageUrl || currentQuestion.fallbackImageUrl) && (
              <div className="game-host-image-section">
                <div className="game-image-container">
                  <ImageWithFallback
                    src={currentQuestion.imageUrl || ''}
                    fallbackSrc={currentQuestion.fallbackImageUrl}
                    alt={t('gameplay.imageAlt.guessPicture')}
                  />
                </div>
              </div>
            )}

            <div
              className={`game-host-question-section${flipState.showQuestionFlip || flipState.isFadingOut ? ' game-host-question-section--showing-fact' : ''}`}
            >
              <QuestionDisplay
                question={currentQuestion as Question}
                showQuestionFlip={flipState.showQuestionFlip}
                isFadingOut={flipState.isFadingOut}
                previousQuestionIdForFlipRef={flipState.previousQuestionIdForFlipRef}
                showHint={false}
                gameType={roomState.gameType}
              />
            </div>

            {hostIsPlaying && (
              <div className="game-host-answers-section">
                <AnswerOptions
                  question={currentQuestion as Question}
                  selectedAnswer={selectedAnswer}
                  answerSubmitted={answerSubmitted}
                  hostIsPlaying={hostIsPlaying}
                  showHint={false}
                  gameType={roomState.gameType}
                  onAnswerSelect={(answer) => {
                    if (!answerSubmitted) setSelectedAnswer(answer);
                  }}
                  onSubmitAnswer={() => {
                    if (selectedAnswer && currentQuestion && !answerSubmitted) {
                      handleAnswerClick(selectedAnswer);
                    }
                  }}
                  answerCorrect={answerCorrect}
                  waitingForOthers={answerSubmitted && !questionResult}
                  isSubmitting={answerSubmitted}
                />
              </div>
            )}
          </div>

          <div className="game-host-right">
            <StandingsSidebar
              players={roomState.players}
              liveAnswers={sharedLiveAnswers}
              totalPlayers={roomState.players.length}
              questionId={currentQuestion.questionId || currentQuestion.id}
              shouldUpdateScores={false}
              theme={{
                primary: THEME.primary,
                primaryLight: THEME.primaryLight,
                accent: THEME.accent,
              }}
            />
          </div>
        </div>
      )}
    </PartyHostPlayingChrome>
  );
};

export default KinkyOrNotHostDisplay;
